/**
 * Usage Service for QA AI Agent
 *
 * Token usage, org AI wallet, and per-prompt history (/api/v1/usage/*).
 */
import type {
  PromptDetail, PromptsPage, UsageQuery, UsageSummary, UsageTimeseries, WalletInfo,
} from '../types/usage';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8003/api/v1';

export const buildUsageParams = (q: Record<string, string | number | undefined>): URLSearchParams => {
  const params = new URLSearchParams();
  Object.entries(q).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.append(k, String(v));
  });
  return params;
};

const getJson = async <T>(path: string, params?: URLSearchParams): Promise<T> => {
  const qs = params && params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(`${API_URL}${path}${qs}`, {
    method: 'GET',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({ detail: response.statusText }));
    throw new Error(error.detail || `Request failed: ${path}`);
  }
  return response.json();
};

export const getWallet = () => getJson<WalletInfo>('/usage/wallet');
export const getUsageSummary = (q: UsageQuery) =>
  getJson<UsageSummary>('/usage/summary', buildUsageParams(q as Record<string, string | number | undefined>));
export const getUsageTimeseries = (q: UsageQuery) =>
  getJson<UsageTimeseries>('/usage/timeseries', buildUsageParams(q as Record<string, string | number | undefined>));
export const listUsagePrompts = (q: UsageQuery & { cursor?: string; limit?: number }) =>
  getJson<PromptsPage>('/usage/prompts', buildUsageParams(q as Record<string, string | number | undefined>));
export const getUsagePrompt = (promptGroupId: string) => getJson<PromptDetail>(`/usage/prompts/${encodeURIComponent(promptGroupId)}`);

export const formatTokens = (n: number): string => {
  if (!n) return '0';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
};

export const formatUsd = (n: number): string => `$${n < 1 ? n.toFixed(3) : n.toFixed(2)}`;

export const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};
